// The partner view's display rules (ART-239 part 2), pure so they are tested without rendering. The spec's
// EVENT_TYPES table, the relative chip, the row and hero copy, and 12-hour times everywhere.
import { formatClock } from "../mobile/dayOfShow.js";
import type { PartnerTravelRow, PartnerViewItem } from "../../app/hooks/usePartner.js";
import type { components } from "@cntrl/api-client";
import type { JourneyLeg } from "../mobile/JourneyStrip.js";

/** The local calendar day as `YYYY-MM-DD` — the server sends from yesterday (its clock is UTC); the screen
 *  shows from THE PARTNER's today. */
export function localIso(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Items from the partner's local today forward, in date order (the server already orders them). */
export function upcoming(items: readonly PartnerViewItem[] | null | undefined, today = localIso()): PartnerViewItem[] {
  return (items ?? []).filter((i) => (i.date ?? "") >= today);
}

function dayDiff(iso: string, today: string): number {
  const a = Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10));
  const b = Date.UTC(+today.slice(0, 4), +today.slice(5, 7) - 1, +today.slice(8, 10));
  return Math.round((a - b) / 86_400_000);
}

/** The hero's relative chip: Today · Tomorrow · In N days (under 14) · In N weeks. */
export function relativeChip(iso: string, today = localIso()): string {
  const n = dayDiff(iso, today);
  if (n <= 0) return "Today";
  if (n === 1) return "Tomorrow";
  if (n < 14) return `In ${n} days`;
  return `In ${Math.round(n / 7)} weeks`;
}

/** "Fri, Oct 9" — the wall-clock day, never shifted by a timezone (the date is a calendar day). */
export function shortDate(iso: string): string {
  const d = new Date(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10));
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

/** "OCT" / "9" — a calendar row's date block. */
export function monthDay(iso: string): { mon: string; day: string } {
  const d = new Date(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10));
  return { mon: d.toLocaleDateString("en-US", { month: "short" }).toUpperCase(), day: String(d.getDate()) };
}

/** 12-hour, always — a rendered "15:00" is a bug (the spec's own words). Empty for nothing. */
export const t12 = (t: string | null | undefined): string => formatClock(t);

/** "12:00 PM – 6:00 PM", or just the start. */
export function tspan(start: string | null | undefined, end: string | null | undefined): string {
  const a = t12(start);
  const b = t12(end);
  return a && b ? `${a} – ${b}` : a;
}

const EVENT_LABEL: Record<string, string> = {
  rehearsal: "Rehearsal",
  write: "Write",
  podcast: "Podcast",
  content: "Content Shoot",
  pr: "PR",
  radio: "Radio",
  meeting: "Meeting",
  placement: "Placement",
};
const EVENT_TIME_LABEL: Record<string, string> = {
  rehearsal: "Time",
  write: "Session",
  podcast: "Record",
  content: "Call time",
  pr: "Starts",
  radio: "On-air",
  meeting: "Starts",
};

/** An event's headline label (the spec's EVENT_TYPES; anything unmapped is "Event"). */
export const eventLabel = (type: string | null | undefined): string => EVENT_LABEL[type ?? ""] ?? "Event";
/** An event's time label ("Starts" for anything unmapped). */
export const eventTimeLabel = (type: string | null | undefined): string => EVENT_TIME_LABEL[type ?? ""] ?? "Starts";

export type ChipTone = "show" | "notLocked" | "rehearsal" | "type" | "off";

/** A calendar row: headline, sub-line, and the chip on the right. One function so the row and its tests agree. */
export function rowModel(item: PartnerViewItem, multiBand: boolean): {
  title: string;
  sub: string;
  chip: string;
  tone: ChipTone;
  route?: { from: string; to: string };
} {
  const band = multiBand && item.bandName ? ` · ${item.bandName}` : "";
  const place = [item.city, item.region].filter(Boolean).join(", ");
  switch (item.kind) {
    case "show":
      return item.confirmed === false
        ? { title: item.title || "Show", sub: place + band, chip: "Not locked", tone: "notLocked" }
        : { title: item.title || "Show", sub: place + band, chip: "Show", tone: "show" };
    case "travel": {
      const from = item.fromCity ?? "";
      const to = item.toCity ?? "";
      return {
        title: from && to ? `${from} → ${to}` : to || from || "Travel day",
        route: from && to ? { from, to } : undefined,
        sub: `Travel day${item.busCall ? ` · bus call ${item.busCall}` : ""}${band}`,
        chip: item.departTime ? `Rolls ${t12(item.departTime)}` : "Travel",
        tone: "off",
      };
    }
    case "off":
      return {
        title: item.city ? `Day off in ${item.city}` : "Day off",
        sub: `No travel${band}`,
        chip: "Free day",
        tone: "off",
      };
    default: {
      const label = eventLabel(item.eventType);
      return {
        title: item.title || label,
        sub: (item.locationName ?? "") + band,
        chip: label,
        tone: item.eventType === "rehearsal" ? "rehearsal" : "type",
      };
    }
  }
}

/** The first name to use in a sentence about the member ("Where Ryan's staying"); "they" reads wrong in a possessive,
 *  so the fallback is the whole phrase, not the pronoun (the spec's "they's" bug). */
export function stayingLabel(firstName: string | null | undefined): string {
  return firstName ? `Where ${firstName}’s staying` : "Where they’re staying";
}

/**
 * The member's FLIGHTS as the crew's journey strip reads them (ART-239 — "the flight trackers should be there"):
 * legs in departure order, and each leg's live status by id. The server attaches the status from the same cache the
 * crew's logistics page reads, so the partner's tracker and the crew's cannot describe one flight differently.
 */
export function partnerFlights(travel: readonly PartnerTravelRow[] | null | undefined): {
  legs: JourneyLeg[];
  statusById: Map<string, components["schemas"]["FlightStatusDto"]>;
} {
  const flights = (travel ?? [])
    .filter((t) => t.kind === "flight")
    .sort((a, b) => (a.departLocal ?? "").localeCompare(b.departLocal ?? ""));
  const statusById = new Map<string, components["schemas"]["FlightStatusDto"]>();
  for (const f of flights) if (f.id && f.flightStatus) statusById.set(f.id, f.flightStatus);
  return {
    legs: flights.map((f) => ({
      travelItemId: f.id,
      airline: f.airline,
      flightNumber: f.flightNumber,
      departAirport: f.departAirport,
      arriveAirport: f.arriveAirport,
      departLocal: f.departLocal,
      arriveLocal: f.arriveLocal,
      departTz: f.departTz,
      arriveTz: f.arriveTz,
    })),
    statusById,
  };
}
